import {useEffect, useMemo} from 'react';
import {usePathname} from 'next/navigation';
import type {Trackables} from './types';
import {
	getAppNameFromPathUrl,
	getIsOauthFromPath,
} from '@app/modules/apps/utils';
import {useIsPaymentJourney} from '@app/modules/redirection/hooks';
import {useRouter} from 'next/router';
import {logGTM} from '.';
import {gtmStrategy} from '../logCenter';
import {usePrevious} from '@deezer/react-utils';
import {useUserData} from '@app/hooks/controllers/useUserData';
import {useAutolog} from '@app/hooks/auth/useAutolog';
import {getUniqId} from '../utils';
import {AuthStatus} from '@app/hooks/auth/useAutolog/utils';
import {useClientCountry} from '@app/hooks/useClientCountry';
import {getTrackAcqPageName} from '@app/utils/offers';

const prettyPathToPageNameMapping: Record<string, string> = {
	signup: 'register',
	'': 'home',
};

export function determinePageTrackingContextFromPath(
	path: string,
	isPaid: boolean,
) {
	const payPrefix = isPaid ? 'pay_' : '';
	const appName = getAppNameFromPathUrl(path);
	const isOauthPrefix = getIsOauthFromPath(path) ? 'oauth_' : '';
	const isAcqPage = path.includes('offers');

	const prettyPath =
		path === `/${appName.toLowerCase()}`
			? 'register'
			: path
					.split('/')
					.filter((pathElement) => {
						return (
							pathElement &&
							pathElement !== 'oauth' &&
							pathElement !== appName.toLowerCase()
						);
					})
					.join('/');
	return {
		pagecategory: isAcqPage ? 'plans' : `${appName.toLowerCase()}_account`,
		pagename: isAcqPage
			? getTrackAcqPageName(path)
			: `${payPrefix}${isOauthPrefix}${
					prettyPathToPageNameMapping[prettyPath] ?? prettyPath
				}`,
	} satisfies Partial<Trackables>;
}

const usePageTrackingContext = () => {
	const pathname = usePathname();
	const isPaid = useIsPaymentJourney();
	const {locale} = useRouter();
	const {status} = useAutolog();
	const {user} = useUserData();
	const {country} = useClientCountry();

	// We still have to use old router unfortunately to make sure in pages that the params have been fully evaluated
	const {isReady} = useRouter();

	return useMemo(() => {
		if (!isReady || status === AuthStatus.loading || !country) {
			return null;
		}
		const userContext = {
			dzr_uniq_id: getUniqId(),
			deezer_user_id: user?.id?.toString() ?? '0',
			country: country ?? 'null',
			offer_id: user?.offerId ? Number(user?.offerId) : 0,
			language: locale ?? 'en',
		};
		const pageContext = determinePageTrackingContextFromPath(pathname, isPaid);
		return {
			...pageContext,
			...userContext,
			eventcategory: pageContext.pagename,
		} satisfies Partial<Trackables>;
	}, [
		isReady,
		status,
		country,
		user?.id,
		user?.offerId,
		locale,
		pathname,
		isPaid,
	]);
};

export function usePageView() {
	const trackingContext = usePageTrackingContext();
	const previous = usePrevious(trackingContext);

	useEffect(() => {
		if (
			!trackingContext ||
			JSON.stringify(trackingContext) === JSON.stringify(previous)
		) {
			return;
		}
		logGTM(
			{
				event: 'pageView',
				...trackingContext,
			},
			true,
		);
		gtmStrategy.openGate();
	}, [trackingContext, previous]);
}
